import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, formatError } from "@/lib/api";
import { toast } from "@/lib/toast";
import { ShieldCheck, Flag, Trash2, Check, ShieldBan, ArrowLeft, Loader2, ExternalLink } from "lucide-react";

const DURATIONS = [
  { key: "24h", label: "24h" },
  { key: "7d", label: "7 days" },
  { key: "30d", label: "30 days" },
  { key: "permanent", label: "Permanent" },
];

function Card({ children, className = "" }) {
  return <div className={`border border-sniper bg-surface ${className}`}>{children}</div>;
}

export default function ModDashboard() {
  const [flags, setFlags] = useState([]);
  const [restrictions, setRestrictions] = useState([]);
  const [summary, setSummary] = useState({ open_flags: 0, active_restrictions: 0 });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("reports");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [f, r, s] = await Promise.all([
        api.get("/forum/mod/flags", { params: { status: "open" } }),
        api.get("/forum/mod/restrictions", { params: { status: "active" } }),
        api.get("/forum/mod/summary"),
      ]);
      setFlags(f.data.flags || []);
      setRestrictions(r.data.restrictions || []);
      setSummary(s.data || {});
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const resolve = async (flag, action) => {
    try {
      await api.post(`/forum/mod/flags/${flag.flag_id}/resolve`, { action });
      toast.success(action === "remove_content" ? "Content removed." : "Report dismissed.");
      await load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const lift = async (r) => {
    try { await api.post(`/forum/mod/restrictions/${r.restriction_id}/lift`, { reason: "" }); toast.success("Restriction lifted."); await load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const tabCls = (active) => `font-mono uppercase text-xs px-4 py-2 border transition-colors ${active ? "bg-olive text-white border-olive" : "border-sniper text-secondary-foreground hover:border-olive/60"}`;

  return (
    <div className="min-h-screen" data-testid="mod-dashboard">
      <Navbar />
      <div className="max-w-[900px] mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <Link to="/forum" data-testid="mod-back" className="inline-flex items-center gap-1.5 font-mono uppercase text-[11px] tracking-wide text-secondary-foreground mb-6 hover:text-foreground transition-colors">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to forum
        </Link>
        <div className="flex items-center gap-3 mb-2">
          <ShieldCheck className="w-7 h-7 text-olive-bright" />
          <h1 className="font-heading text-4xl sm:text-5xl font-black uppercase tracking-tight" data-testid="mod-title">Forum Moderation</h1>
        </div>
        <p className="font-mono text-xs text-secondary-foreground mb-6">Reports and member restrictions for the community forum. Forum-only — this tool grants no other platform access.</p>

        <div className="flex gap-2 mb-6">
          <button onClick={() => setTab("reports")} data-testid="mod-tab-reports" className={tabCls(tab === "reports")}>Reports <span className="tabular-nums">({summary.open_flags})</span></button>
          <button onClick={() => setTab("restrictions")} data-testid="mod-tab-restrictions" className={tabCls(tab === "restrictions")}>Restrictions <span className="tabular-nums">({summary.active_restrictions})</span></button>
        </div>

        {loading ? (
          <div className="py-16 text-center text-secondary-foreground"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : tab === "reports" ? (
          <div className="flex flex-col gap-3" data-testid="mod-reports">
            {flags.length === 0 ? (
              <Card className="p-10 text-center" data-testid="mod-reports-empty">
                <Flag className="w-8 h-8 mx-auto mb-2 text-secondary-foreground" strokeWidth={1.4} />
                <p className="font-heading uppercase font-black text-lg">No open reports</p>
                <p className="font-mono text-xs text-secondary-foreground mt-1">The queue is clear.</p>
              </Card>
            ) : flags.map((f) => (
              <Card key={f.flag_id} className="p-4">
                <div data-testid={`mod-flag-${f.flag_id}`} className="flex flex-col gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-[10px] uppercase tracking-[1.5px] px-2 py-0.5 border border-blaze/50 text-blaze">{f.reason_label}</span>
                    <span className="font-mono text-[10px] uppercase tracking-wide px-2 py-0.5 border border-sniper text-secondary-foreground">{f.target_type}</span>
                    <span className="font-mono text-[11px] text-secondary-foreground">reported by @{f.reporter_username} · author @{f.author_username || "—"}</span>
                  </div>
                  {f.snapshot?.title && <p className="font-body font-bold text-sm text-foreground">{f.snapshot.title}</p>}
                  <p className="font-body text-sm text-secondary-foreground whitespace-pre-wrap line-clamp-4">{f.snapshot?.body || "(no content)"}</p>
                  {f.note && <p className="font-mono text-[11px] text-secondary-foreground italic">Reporter note: {f.note}</p>}
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    {f.thread_id && (
                      <Link to={`/forum/thread/${f.thread_id}`} data-testid={`mod-flag-view-${f.flag_id}`} className="inline-flex items-center gap-1 font-mono text-[11px] uppercase text-olive-bright hover:underline">
                        <ExternalLink className="w-3.5 h-3.5" /> View
                      </Link>
                    )}
                    <button onClick={() => resolve(f, "dismiss")} data-testid={`mod-flag-dismiss-${f.flag_id}`} className="ml-auto inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wide px-3 py-1.5 border border-sniper text-secondary-foreground hover:text-foreground transition-colors">
                      <Check className="w-3.5 h-3.5" /> Dismiss
                    </button>
                    <button onClick={() => resolve(f, "remove_content")} data-testid={`mod-flag-remove-${f.flag_id}`} className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wide px-3 py-1.5 border text-white transition-colors" style={{ background: "#a33d28", borderColor: "#a33d28" }}>
                      <Trash2 className="w-3.5 h-3.5" /> Remove content
                    </button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <div data-testid="mod-restrictions">
            <RestrictForm onDone={load} />
            <div className="flex flex-col gap-2 mt-4">
              {restrictions.length === 0 ? (
                <Card className="p-10 text-center" data-testid="mod-restrictions-empty">
                  <ShieldBan className="w-8 h-8 mx-auto mb-2 text-secondary-foreground" strokeWidth={1.4} />
                  <p className="font-heading uppercase font-black text-lg">No active restrictions</p>
                </Card>
              ) : restrictions.map((r) => (
                <Card key={r.restriction_id} className="p-3 flex items-center justify-between gap-3">
                  <div data-testid={`mod-restriction-${r.restriction_id}`}>
                    <p className="font-mono text-sm text-foreground">
                      <span className={r.kind === "ban" ? "text-blaze" : "text-amber-400"}>{(r.kind || "").toUpperCase()}</span> · @{r.username}
                    </p>
                    <p className="font-mono text-[11px] text-secondary-foreground">
                      {r.expires_at ? `until ${String(r.expires_at).slice(0, 16).replace("T", " ")} UTC` : "permanent"} · by @{r.by_username} · {r.reason}
                    </p>
                  </div>
                  <button onClick={() => lift(r)} data-testid={`mod-restriction-lift-${r.restriction_id}`} className="font-mono text-[10px] uppercase tracking-wide px-3 py-1.5 border border-sniper text-secondary-foreground hover:text-foreground transition-colors shrink-0">Lift</button>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function RestrictForm({ onDone }) {
  const [username, setUsername] = useState("");
  const [kind, setKind] = useState("mute");
  const [duration, setDuration] = useState("24h");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!username.trim()) { toast.error("Enter a member username."); return; }
    if (reason.trim().length < 3) { toast.error("Add a short reason."); return; }
    setBusy(true);
    try {
      const handle = username.trim().replace(/^@/, "");
      await api.post("/forum/mod/restrict", { username: handle, kind, duration, reason: reason.trim() });
      toast.success(`@${handle} ${kind === "ban" ? "banned" : "muted"}.`);
      setUsername(""); setReason("");
      onDone && onDone();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const pill = (active) => `font-mono text-xs px-3 py-2 border transition-colors ${active ? "bg-olive text-white border-olive" : "border-sniper text-secondary-foreground hover:border-olive/60"}`;
  return (
    <Card className="p-4" >
      <p className="font-heading uppercase font-black text-sm mb-3 text-foreground">Restrict a member</p>
      <div className="grid sm:grid-cols-2 gap-2">
        <input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="@username" data-testid="mod-restrict-username" className="bg-black/40 border border-sniper focus:border-olive px-3 py-2 text-sm text-foreground outline-none" />
        <div className="flex gap-2">
          {["mute", "ban"].map((k) => (
            <button key={k} onClick={() => setKind(k)} data-testid={`mod-restrict-kind-${k}`} className={`flex-1 uppercase ${pill(kind === k)}`}>{k}</button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
        {DURATIONS.map((d) => (
          <button key={d.key} onClick={() => setDuration(d.key)} data-testid={`mod-restrict-duration-${d.key}`} className={pill(duration === d.key)}>{d.label}</button>
        ))}
      </div>
      <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={500} placeholder="Reason (logged)" data-testid="mod-restrict-reason" className="w-full bg-black/40 border border-sniper focus:border-olive px-3 py-2 text-sm text-foreground mt-2 resize-y outline-none" />
      <button onClick={submit} disabled={busy} data-testid="mod-restrict-submit" className="mt-2 inline-flex items-center gap-2 font-mono uppercase text-xs font-bold tracking-wide px-4 py-2 text-white transition-colors disabled:opacity-50" style={{ background: "#a33d28" }}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldBan className="w-4 h-4" />} Apply
      </button>
    </Card>
  );
}
